import React, { useState, useEffect } from 'react';
import { getCachedData, setCachedData } from '../utils/apiCache';
import { Card, Row, Col, Badge, Spinner, Alert, Container, Button } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaMoneyBillWave,
  FaSync,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaCalendarTimes,
  FaCog,
  FaArrowLeft,
  FaDownload,
  FaEdit,
  FaChartLine,
  FaReceipt,
  FaUserClock,
  FaDatabase
} from 'react-icons/fa';
import moment from 'moment';
import Swal from 'sweetalert2';
import Header from './Header';
import { Link } from 'react-router-dom';
import { all_routes } from './router/all_routes';

const FeeCycle = () => {
  const [feeCycleData, setFeeCycleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const feeCycleId = localStorage.getItem('fee_cycle_id');
  const routes = all_routes;

  const fetchFeeCycleData = async () => {
    if (!feeCycleId) {
      setError('Fee Cycle ID not found in local storage');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authtoken = localStorage.getItem('token');

      const response = await fetch(
        `https://api.mirai.healthcare/api/fee-service/fee-cycles/${feeCycleId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-auth': authtoken
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFeeCycleData(data);
    } catch (err) {
      console.error('Error fetching fee cycle data:', err);
      setError(err.message || 'Failed to fetch fee cycle data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchFeeCycleData();
  }, [feeCycleId]);

  // Edit Cycle Settings Function
  const handleEditCycle = () => {
    Swal.fire({
      icon: 'info',
      title: 'Coming Soon',
      text: 'Edit Cycle Settings feature will be available in the next update.',
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6',
      customClass: {
        popup: 'coming-soon-popup'
      }
    });
  };

  // Export Function
  const handleExport = () => {
    Swal.fire({
      icon: 'success',
      title: 'Export Started',
      text: 'Fee cycle data export has been initiated. You will receive the file shortly.',
      confirmButtonText: 'OK',
      confirmButtonColor: '#28a745',
      timer: 3000,
      showConfirmButton: true
    });
  };

  // Refresh Function
  const handleRefresh = () => {
    setRefreshing(true);
    fetchFeeCycleData();

    // Show success message after refresh
    setTimeout(() => {
      Swal.fire({
        icon: 'success',
        title: 'Refreshed',
        text: 'Fee cycle data has been refreshed successfully.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#17a2b8',
        timer: 2000,
        showConfirmButton: false
      });
    }, 500);
  };

  // Back Function
  const handleBack = () => {
    window.history.back();
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'expired':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getTypeDisplay = (type) => {
    const typeMap = {
      'halfyearly': 'Half Yearly',
      'quarterly': 'Quarterly',
      'monthly': 'Monthly',
      'annual': 'Annual',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  const getMonthName = (monthNumber) => {
    return moment().month(monthNumber - 1).format('MMMM');
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('DD MMMM YYYY');
  };

  const calculateProgress = () => {
    if (!feeCycleData) return 0;

    const start = moment(feeCycleData.session_start_date);
    const end = moment(feeCycleData.session_end_date);
    const now = moment();

    const totalDuration = end.diff(start, 'days');
    const elapsed = now.diff(start, 'days');

    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary', trend }) => (
    <Card className="stat-card border-0 h-100">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className={`icon-container bg-${color} mb-3`}>
              <Icon size={20} className="text-white" />
            </div>
            <h6 className="text-muted mb-2">{title}</h6>
            <h3 className="fw-bold text-dark mb-1">{value}</h3>
            {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
            {trend && (
              <Badge bg={trend > 0 ? 'success' : 'danger'} className="mt-2">
                {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
              </Badge>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const InfoItem = ({ icon: Icon, label, value, subtext, color = 'primary' }) => (
    <div className="info-item d-flex align-items-start p-3 border-bottom">
      <div className={`icon-container bg-${color}-subtle me-3 flex-shrink-0`}>
        <Icon size={18} className={`text-${color}`} />
      </div>
      <div className="flex-grow-1">
        <label className="text-muted small mb-1">{label}</label>
        <h6 className="fw-bold text-dark mb-0">{value}</h6>
        {subtext && <small className="text-muted">{subtext}</small>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Header />
          <Container fluid className="py-5">
            <div className="text-center py-5">
              <div className="loading-spinner">
                <FaMoneyBillWave size={48} className="text-primary mb-3" />
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5 className="text-dark">Loading Fee Cycle</h5>
                <p className="text-muted">Fetching cycle details...</p>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (error || !feeCycleData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Header />
          <Container fluid className="py-5">
            <div className="error-state text-center">
              <FaExclamationTriangle size={64} className="text-danger mb-3 opacity-50" />
              <h4 className="text-dark mb-3">Unable to Load Fee Cycle</h4>
              <p className="text-muted mb-4">{error || 'No fee cycle data available'}</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="primary" onClick={fetchFeeCycleData}>
                  <FaSync className="me-2" />
                  Try Again
                </Button>
                <Button variant="outline-secondary" onClick={handleBack}>
                  <FaArrowLeft className="me-2" />
                  Go Back
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="page-wrapper">
      <div className="content">
        <Header />

        <Container fluid className="py-4">
          {/* Modern Header */}
          <div className="page-header-simple mb-5">
            <div className="header-content">
              {/* Breadcrumb */}
              {/* <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb simple-breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard} className="text-decoration-none text-muted">
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.feeManagement} className="text-decoration-none text-muted">
                      Finance
                    </Link>
                  </li>
                  <li className="breadcrumb-item active text-primary">Fee Cycles</li>
                </ol>
              </nav> */}

              {/* Main Header Row */}
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-3">
                {/* Title Section */}
                <div className="d-flex align-items-center gap-3">
                  <div className="header-icon-simple">
                    <FaMoneyBillWave size={24} className="text-primary" />
                  </div>
                  <div>
                    <h2
              className="page-title mb-1 w-bold mb-0"
              style={{ letterSpacing: "0.5px" }}>{feeCycleData.name}</h2>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge
                        bg={getStatusVariant(feeCycleData.status)}
                        className="status-badge"
                      >
                        {feeCycleData.status}
                      </Badge>
                      <span className="text-muted small">
                        <FaDatabase className="me-1" size={12} />
                        ID: {feeCycleData.id}
                      </span>
                      <span className="text-muted small">
                        <FaSync className="me-1" size={12} />
                        {getTypeDisplay(feeCycleData.type)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-2 action-buttons">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="btn-action"
                    onClick={handleEditCycle}
                  >
                    <FaEdit className="me-1" size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="btn-action"
                    onClick={handleExport}
                  >
                    <FaDownload className="me-1" size={14} />
                    Export
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="btn-action"
                    onClick={handleBack}
                  >
                    <FaArrowLeft className="me-1" size={14} />
                    Back
                  </Button>
                </div>
              </div>

              {/* Progress Section */}
              <div className="progress-section-simple mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Cycle Progress</span>
                  <span className="fw-bold text-primary small">{progress.toFixed(1)}% Complete</span>
                </div>
                <div className="progress simple-progress">
                  <div
                    className="progress-bar simple-progress-bar"
                    style={{ width: `${progress}%` }}
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">
                    <FaCalendarAlt className="me-1" size={12} />
                    {formatDate(feeCycleData.session_start_date)}
                  </small>
                  <small className="text-muted">
                    <FaCalendarAlt className="me-1" size={12} />
                    {formatDate(feeCycleData.session_end_date)}
                  </small>
                </div>
              </div>
            </div>
          </div>

          <style jsx>{`
  .page-header-simple {
    background: #ffffff;
    border: 1px solid #e9ecef;
    padding: 1.5rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .simple-breadcrumb {
    background: transparent;
    padding: 0;
    margin-bottom: 0;
  }

  .simple-breadcrumb .breadcrumb-item a {
    color: #6c757d !important;
    font-size: 0.875rem;
    text-decoration: none;
  }

  .simple-breadcrumb .breadcrumb-item.active {
    color: #495057 !important;
    font-weight: 500;
  }

  .simple-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
    color: #6c757d;
    content: "/";
    padding: 0 0.5rem;
  }

  .header-icon-simple {
    background: #f8f9fa;
    padding: 0.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e9ecef;
  }

  .page-title-simple {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: #212529;
    line-height: 1.2;
  }

  .status-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-weight: 500;
  }

  .action-buttons {
    flex-shrink: 0;
  }

  .btn-action {
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .progress-section-simple {
    background: #f8f9fa;
    padding: 1rem 1.25rem;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .simple-progress {
    height: 6px;
    border-radius: 10px;
    background: #e9ecef;
    overflow: hidden;
  }

  .simple-progress-bar {
    background: #007bff;
    border-radius: 10px;
    transition: width 0.5s ease;
  }

  /* Mobile Responsive */
  @media (max-width: 768px) {
    .page-header-simple {
      padding: 1.25rem;
      margin-bottom: 1.5rem;
    }

    .page-title-simple {
      font-size: 1.25rem;
    }

    .header-content {
      gap: 1rem;
    }

    .d-flex.flex-column.flex-md-row {
      gap: 1rem !important;
    }

    .d-flex.align-items-center.gap-3 {
      gap: 1rem !important;
    }

    .header-icon-simple {
      padding: 0.5rem;
    }

    .header-icon-simple svg {
      size: 20px;
    }

    .action-buttons {
      width: 100%;
      justify-content: center;
    }

    .btn-action {
      flex: 1;
      min-width: 0;
      padding: 0.5rem 0.75rem;
    }

    .progress-section-simple {
      padding: 0.875rem 1rem;
    }

    .d-flex.justify-content-between.mt-2 {
      flex-direction: column;
      gap: 0.5rem;
      text-align: center;
    }

    .d-flex.justify-content-between.mt-2 small {
      font-size: 0.75rem;
    }
  }

  @media (max-width: 576px) {
    .page-header-simple {
      padding: 1rem;
    }

    .page-title-simple {
      font-size: 1.125rem;
    }

    .d-flex.align-items-center.gap-2 {
      gap: 0.5rem !important;
    }

    .status-badge {
      font-size: 0.7rem;
      padding: 0.2rem 0.6rem;
    }

    .text-muted.small {
      font-size: 0.75rem;
    }

    .action-buttons {
      flex-wrap: wrap;
    }

    .btn-action {
      flex: 0 0 calc(50% - 0.25rem);
      margin-bottom: 0.5rem;
    }
  }

  @media (max-width: 400px) {
    .action-buttons {
      flex-direction: column;
    }

    .btn-action {
      flex: 1;
      width: 100%;
      margin-bottom: 0.5rem;
    }

    .d-flex.align-items-center.gap-3 {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem !important;
    }

    .header-icon-simple {
      align-self: flex-start;
    }
  }
`}</style>

          {/* Quick Stats */}
          <Row className="g-4 mb-5">
            <Col xl={3} md={6}>
              <StatCard
                icon={FaSync}
                title="Cycle Type"
                value={getTypeDisplay(feeCycleData.type)}
                color="primary"
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon={FaClock}
                title="Duration"
                value={`${moment(feeCycleData.session_end_date).diff(
                  moment(feeCycleData.session_start_date),
                  'months'
                )} Months`}
                subtitle="Total cycle period"
                color="info"
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon={FaCalendarCheck}
                title="Due Date"
                value={`${feeCycleData.selected_due_date}th`}
                subtitle={getMonthName(feeCycleData.selected_due_month)}
                color="warning"
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon={FaUserClock}
                title="Status"
                value={feeCycleData.status}
                subtitle="Current cycle status"
                color="success"
              />
            </Col>
          </Row>

          {/* Main Content */}
          <Row className="g-4">
            {/* Left Column - Details */}
            <Col lg={8}>
              <Card className="modern-card border-0">
                <Card.Header className="modern-card-header">
                  <h5 className="mb-0">
                    <FaCog className="me-3 text-primary" />
                    Cycle Configuration
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <InfoItem
                    icon={FaCalendarAlt}
                    label="Session Period"
                    value={`${formatDate(feeCycleData.session_start_date)} - ${formatDate(feeCycleData.session_end_date)}`}
                    subtext={`${getMonthName(feeCycleData.session_start_month)} to ${getMonthName(feeCycleData.session_end_month)}`}
                    color="primary"
                  />
                  <InfoItem
                    icon={FaCalendarCheck}
                    label="Due Date Schedule"
                    value={`Every ${feeCycleData.selected_due_date}th of ${getMonthName(feeCycleData.selected_due_month)}`}
                    subtext="Payment due date"
                    color="success"
                  />
                  <InfoItem
                    icon={FaReceipt}
                    label="Cycle Type"
                    value={getTypeDisplay(feeCycleData.type)}
                    subtext="Fee collection frequency"
                    color="info"
                  />
                  <InfoItem
                    icon={FaDatabase}
                    label="Session ID"
                    value={`#${feeCycleData.session_id}`}
                    subtext="Associated academic session"
                    color="secondary"
                  />
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column - Timeline & Actions */}
            <Col lg={4}>
              <Card className="modern-card border-0">
                <Card.Header className="modern-card-header">
                  <h5 className="mb-0">
                    <FaCalendarAlt className="me-3 text-primary" />
                    Key Dates
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="timeline">
                    <div className="timeline-item start">
                      <div className="timeline-marker bg-success"></div>
                      <div className="timeline-content">
                        <h6 className="fw-bold mb-1">Session Start</h6>
                        <p className="text-muted mb-0 small">
                          {formatDate(feeCycleData.session_start_date)}
                        </p>
                      </div>
                    </div>
                    <div className="timeline-item due">
                      <div className="timeline-marker bg-warning"></div>
                      <div className="timeline-content">
                        <h6 className="fw-bold mb-1">Due Date</h6>
                        <p className="text-muted mb-0 small">
                          {feeCycleData.selected_due_date}th {getMonthName(feeCycleData.selected_due_month)}
                        </p>
                      </div>
                    </div>
                    <div className="timeline-item end">
                      <div className="timeline-marker bg-danger"></div>
                      <div className="timeline-content">
                        <h6 className="fw-bold mb-1">Session End</h6>
                        <p className="text-muted mb-0 small">
                          {formatDate(feeCycleData.session_end_date)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Quick Actions */}
              <Card className="modern-card border-0 mt-4">
                <Card.Header className="modern-card-header">
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      className="text-start d-flex align-items-center"
                      onClick={handleEditCycle}
                    >
                      <FaEdit className="me-2" />
                      Edit Cycle Settings
                    </Button>
                    <Button
                      variant="outline-success"
                      className="text-start d-flex align-items-center"
                      onClick={handleExport}
                    >
                      <FaDownload className="me-2" />
                      Export Cycle Data
                    </Button>
                    <Button
                      variant="outline-info"
                      className="text-start d-flex align-items-center"
                      onClick={handleRefresh}
                      disabled={refreshing}
                    >
                      <FaSync className={`me-2 ${refreshing ? 'fa-spin' : ''}`} />
                      {refreshing ? 'Refreshing...' : 'Refresh Data'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="text-start d-flex align-items-center"
                      onClick={handleBack}
                    >
                      <FaArrowLeft className="me-2" />
                      Go Back
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <style jsx>{`
          .page-header-modern {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem;
            border-radius: 20px;
            margin-top: 1rem;
          }

          .modern-breadcrumb {
            background: transparent;
            padding: 0;
            margin-bottom: 1rem;
          }

          .modern-breadcrumb .breadcrumb-item a {
            color: rgba(255,255,255,0.8) !important;
            text-decoration: none;
          }

          .modern-breadcrumb .breadcrumb-item.active {
            color: white !important;
          }

          .page-title-modern {
            font-size: 2.5rem;
            font-weight: 700;
            margin: 0;
            color: white;
          }

          .header-icon {
            background: rgba(255,255,255,0.2);
            padding: 1rem;
            border-radius: 15px;
            backdrop-filter: blur(10px);
          }

          .btn-modern {
            border-radius: 10px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            border: none;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }

          .btn-modern:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0,0,0,0.15);
          }

          .progress-section {
            background: rgba(255,255,255,0.1);
            padding: 1.5rem;
            border-radius: 15px;
            backdrop-filter: blur(10px);
          }

          .modern-progress {
            height: 8px;
            border-radius: 10px;
            background: rgba(255,255,255,0.2);
          }

          .modern-progress .progress-bar {
            background: linear-gradient(90deg, #00b09b, #96c93d);
            border-radius: 10px;
            transition: width 0.5s ease;
          }

          .modern-card {
            border-radius: 15px;
            box-shadow: 0 5px 25px rgba(0,0,0,0.08);
            overflow: hidden;
            transition: all 0.3s ease;
          }

          .modern-card:hover {
            box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          }

          .modern-card-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 1px solid #e9ecef;
            padding: 1.5rem;
          }

          .stat-card {
            border-radius: 15px;
            transition: all 0.3s ease;
            background: white;
            box-shadow: 0 5px 20px rgba(0,0,0,0.08);
          }

          .stat-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(0,0,0,0.15);
          }

          .icon-container {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }

          .info-item {
            transition: all 0.3s ease;
            border-bottom: 1px solid #f8f9fa;
          }

          .info-item:hover {
            background: #f8f9fa;
            transform: translateX(5px);
          }

          .info-item:last-child {
            border-bottom: none;
          }

          .timeline {
            position: relative;
            padding-left: 2rem;
          }

          .timeline::before {
            content: '';
            position: absolute;
            left: 15px;
            top: 0;
            bottom: 0;
            width: 2px;
            background: #e9ecef;
          }

          .timeline-item {
            position: relative;
            margin-bottom: 2rem;
          }

          .timeline-marker {
            position: absolute;
            left: -2rem;
            top: 0.25rem;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 0 0 3px;
          }

          .loading-spinner {
            max-width: 400px;
            margin: 0 auto;
          }

          .error-state {
            max-width: 500px;
            margin: 0 auto;
          }

          .coming-soon-popup {
            border-radius: 15px;
          }

          @media (max-width: 768px) {
            .page-header-modern {
              padding: 1.5rem;
              margin-top: 0.5rem;
            }

            .page-title-modern {
              font-size: 2rem;
            }

            .d-flex.gap-3 {
              flex-direction: column;
              align-items: flex-start;
            }

            .header-icon {
              margin-bottom: 1rem;
            }

            .d-flex.gap-2 {
              flex-wrap: wrap;
            }

            .btn-modern {
              margin-bottom: 0.5rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default FeeCycle;